import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { apiFetch } from '../jscontrol/apiClient';
import { getImageSrc } from '../../utils/appConfig';
import { uploadImageFiles } from '../utils/imageUpload';

const makePendingItem = (file, displayOrder, isPrimary) => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  file,
  previewUrl: URL.createObjectURL(file),
  displayOrder,
  isPrimary,
});

const BundleProductImageManage = forwardRef(({ bundleProductId }, ref) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [toast, setToast] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ IsPrimary: false, DisplayOrder: 0 });
  const [previewUrl, setPreviewUrl] = useState('');
  const [pendingItems, setPendingItems] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const formRef = useRef(null);
  const toastTimer = useRef(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  };

  const fetchImages = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiFetch(`/BundleProductImage/GetByBundleProductId/${bundleProductId}`);
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setImages(list);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (bundleProductId) fetchImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundleProductId]);

  useImperativeHandle(ref, () => ({
    saveTab: () => {
      if (showForm && formRef.current) {
        formRef.current.requestSubmit();
        return true;
      }
      showToast('Click "Add Image" to upload new images.', 'danger');
      return false;
    },
  }));

  const openAddForm = () => {
    setEditItem(null);
    setForm({ IsPrimary: false, DisplayOrder: images.length + 1 });
    setPreviewUrl('');
    setPendingItems([]);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setShowForm(true);
  };

  const openEditForm = (img) => {
    setEditItem(img);
    setForm({
      IsPrimary: img.IsPrimary || false,
      DisplayOrder: img.DisplayOrder || 0,
    });
    setPreviewUrl(getImageSrc(img.ImageUrl));
    setPendingItems([]);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setShowForm(true);
  };

  const closeForm = () => {
    pendingItems.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    setShowForm(false);
    setEditItem(null);
    setPreviewUrl('');
    setPendingItems([]);
    setSelectedFile(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (editItem) {
      const file = files[0];
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setPendingItems([]);
      return;
    }

    pendingItems.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    const baseOrder = images.length + 1;
    const items = files.map((file, index) =>
      makePendingItem(file, baseOrder + index, index === 0)
    );
    setPendingItems(items);
    setPreviewUrl('');
    setSelectedFile(null);
  };

  const updatePendingItem = (key, patch) => {
    setPendingItems((prev) =>
      prev.map((item) => (item.key === key ? { ...item, ...patch } : item))
    );
  };

  const setPendingPrimary = (key) => {
    setPendingItems((prev) =>
      prev.map((item) => ({ ...item, isPrimary: item.key === key }))
    );
  };

  const removePendingItem = (key) => {
    setPendingItems((prev) => {
      const target = prev.find((item) => item.key === key);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((item) => item.key !== key);
    });
  };

  const clearPendingSelection = () => {
    pendingItems.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    setPendingItems([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editItem && pendingItems.length === 0) {
      showToast('Please select image(s).', 'danger');
      return;
    }
    setSaving(true);
    try {
      if (editItem) {
        let imageUrl = editItem.ImageUrl;
        if (selectedFile) {
          setUploading(true);
          const urls = await uploadImageFiles('/BundleProductImage', [selectedFile]);
          setUploading(false);
          imageUrl = urls[0];
        }
        await apiFetch('/BundleProductImage/Update', {
          method: 'PUT',
          data: {
            BundleProductImageId: editItem.BundleProductImageId,
            ImageUrl: imageUrl,
            IsPrimary: form.IsPrimary,
            DisplayOrder: Number(form.DisplayOrder) || 0,
            UpdatedBy: 0,
          },
        });
        showToast('Image updated successfully.');
      } else {
        setUploading(true);
        const files = pendingItems.map((item) => item.file);
        const urls = await uploadImageFiles('/BundleProductImage', files);
        setUploading(false);

        for (let i = 0; i < urls.length; i++) {
          const pending = pendingItems[i];
          await apiFetch('/BundleProductImage/Create', {
            method: 'POST',
            data: {
              BundleProductId: Number(bundleProductId),
              ImageUrl: urls[i],
              IsPrimary: pending.isPrimary,
              DisplayOrder: Number(pending.displayOrder) || i + 1,
              CreatedBy: 0,
            },
          });
        }
        showToast(`${urls.length} image(s) saved successfully.`);
      }
      closeForm();
      fetchImages();
    } catch (err) {
      setUploading(false);
      showToast(err.message, 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (img) => {
    if (!window.confirm('Delete this image?')) return;
    setDeleting(img.BundleProductImageId);
    try {
      await apiFetch(`/BundleProductImage/Delete/${img.BundleProductImageId}`, { method: 'DELETE' });
      showToast('Image deleted.');
      fetchImages();
    } catch (err) {
      showToast(err.message, 'danger');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      {toast && (
        <div
          className={`alert alert-${toast.type} py-2 px-3`}
          style={{ fontSize: '0.83rem', position: 'absolute', top: 0, right: 0, zIndex: 10, minWidth: 220 }}
        >
          {toast.msg}
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0 fw-semibold">Bundle Product Images</h6>
        <button type="button" className="btn btn-primary btn-sm" onClick={openAddForm}>
          <i className="mdi mdi-plus me-1"></i>Add Image
        </button>
      </div>

      {showForm && (
        <div className="card mb-4 border" style={{ background: '#f8f9fa' }}>
          <div className="card-body py-3">
            <h6 className="mb-3">{editItem ? 'Edit Image' : 'Add New Images'}</h6>
            <form ref={formRef} onSubmit={handleSubmit}>
              <div className="form-group mb-3">
                <label className="form-label">
                  Select Image(s) <span className="text-danger">*</span>
                  {editItem && (
                    <span className="text-muted ms-2" style={{ fontSize: '0.78rem' }}>
                      (leave blank to keep existing)
                    </span>
                  )}
                </label>

                <div
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  style={{
                    border: '2px dashed #6f42c1',
                    borderRadius: 8,
                    padding: '24px 16px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: '#faf8ff',
                  }}
                >
                  {editItem && previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="preview"
                      style={{ maxHeight: 140, maxWidth: '100%', objectFit: 'contain', borderRadius: 4 }}
                    />
                  ) : (
                    <>
                      <i className="mdi mdi-cloud-upload" style={{ fontSize: '2rem', color: '#6f42c1' }}></i>
                      <div style={{ marginTop: 8, fontSize: '0.85rem', color: '#6c757d' }}>
                        Click to select image(s)
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#adb5bd' }}>
                        JPG, PNG, WEBP — select multiple at once
                      </div>
                    </>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple={!editItem}
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />

                {!editItem && pendingItems.length > 0 && (
                  <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                        {pendingItems.length} image(s) selected — set order & primary for each
                      </span>
                      <button type="button" className="btn btn-link btn-sm text-danger p-0" onClick={clearPendingSelection}>
                        Clear all
                      </button>
                    </div>
                    <div className="row g-3">
                      {pendingItems.map((item, index) => (
                        <div className="col-12 col-md-6 col-lg-4" key={item.key}>
                          <div
                            className="card h-100"
                            style={{
                              border: item.isPrimary ? '2px solid #6f42c1' : '1px solid #dee2e6',
                              borderRadius: 8,
                            }}
                          >
                            <div
                              style={{
                                background: '#f8f9fa',
                                height: 120,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 8,
                              }}
                            >
                              <img
                                src={item.previewUrl}
                                alt={`pending-${index + 1}`}
                                style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                              />
                            </div>
                            <div className="card-body py-2 px-3">
                              <div className="mb-2">
                                <label className="form-label mb-1" style={{ fontSize: '0.78rem' }}>
                                  Display Order
                                </label>
                                <input
                                  className="form-control form-control-sm"
                                  type="number"
                                  min={0}
                                  value={item.displayOrder}
                                  onChange={(e) =>
                                    updatePendingItem(item.key, {
                                      displayOrder: Number(e.target.value) || 0,
                                    })
                                  }
                                />
                              </div>
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`pending-primary-${item.key}`}
                                  checked={item.isPrimary}
                                  onChange={() => setPendingPrimary(item.key)}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`pending-primary-${item.key}`}
                                  style={{ fontSize: '0.82rem' }}
                                >
                                  Set as Primary
                                </label>
                              </div>
                              <button
                                type="button"
                                className="btn btn-link btn-sm text-danger p-0 mt-1"
                                onClick={() => removePendingItem(item.key)}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {editItem && previewUrl && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-danger p-0 mt-1"
                    onClick={() => {
                      setPreviewUrl(getImageSrc(editItem.ImageUrl));
                      setSelectedFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                  >
                    <i className="mdi mdi-close me-1"></i>
                    Revert to original
                  </button>
                )}
              </div>

              {editItem && (
                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label">Display Order</label>
                    <input
                      className="form-control"
                      type="number"
                      name="DisplayOrder"
                      value={form.DisplayOrder}
                      onChange={handleChange}
                      min={0}
                    />
                  </div>
                  <div className="col-6 d-flex align-items-end pb-1">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="IsPrimary"
                        id="img_isPrimary"
                        checked={form.IsPrimary}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="img_isPrimary">
                        Set as Primary
                      </label>
                    </div>
                  </div>
                </div>
              )}

              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving || uploading}>
                  {uploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                      Uploading...
                    </>
                  ) : saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={closeForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger py-2" style={{ fontSize: '0.85rem' }}>
          <i className="mdi mdi-alert-circle me-1"></i>
          {error}
        </div>
      ) : images.length === 0 ? (
        <div className="text-center text-muted py-5" style={{ fontSize: '0.9rem' }}>
          <i className="mdi mdi-image-off" style={{ fontSize: '2rem', display: 'block', marginBottom: 8 }}></i>
          No images yet. Click &quot;Add Image&quot; to upload.
        </div>
      ) : (
        <div className="row g-3">
          {images.map((img) => (
            <div className="col-6 col-sm-4 col-md-3" key={img.BundleProductImageId}>
              <div
                className="card h-100 text-center"
                style={{
                  border: img.IsPrimary ? '2px solid #6f42c1' : '1px solid #dee2e6',
                  borderRadius: 8,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    background: '#f8f9fa',
                    height: 140,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 8,
                  }}
                >
                  <img
                    src={getImageSrc(img.ImageUrl)}
                    alt={`bundle-img-${img.BundleProductImageId}`}
                    style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                <div className="card-body py-2 px-2">
                  <div style={{ fontSize: '0.75rem', color: img.IsPrimary ? '#6f42c1' : '#adb5bd' }}>
                    {img.IsPrimary ? 'Primary' : 'Secondary'}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>Order: {img.DisplayOrder}</div>
                  <div className="d-flex gap-1 justify-content-center mt-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      style={{ fontSize: '0.73rem', padding: '2px 8px' }}
                      onClick={() => openEditForm(img)}
                    >
                      <i className="mdi mdi-pencil"></i>
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      style={{ fontSize: '0.73rem', padding: '2px 8px' }}
                      onClick={() => handleDelete(img)}
                      disabled={deleting === img.BundleProductImageId}
                    >
                      {deleting === img.BundleProductImageId ? (
                        <span className="spinner-border spinner-border-sm" role="status"></span>
                      ) : (
                        <i className="mdi mdi-delete"></i>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

export default BundleProductImageManage;
